import { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useProfileRole } from '@/hooks/useProfileRole';
import { notify } from '@/lib/dashboard-v2/notify';
import { isPlatformAdminRole } from '@/lib/platformRole';
import { Loader2 } from 'lucide-react';

interface AdminGuardProps {
    children: ReactNode;
}

export function AdminGuard({ children }: AdminGuardProps) {
    const { user, loading: authLoading } = useAuth();
    const userId = user?.id ?? null;
    const navigate = useNavigate();
    const { data: role, isPending: rolePending, isError } = useProfileRole();
    const [checking, setChecking] = useState(true);

    useEffect(() => {
        if (authLoading || (userId && rolePending)) return;

        if (!userId) {
            notify.error('Accès refusé', {
                description: 'Vous devez être connecté pour accéder à cette page.'
            });
            navigate('/login');
            setChecking(false);
            return;
        }

        if (isError || !isPlatformAdminRole(role)) {
            notify.error('Accès interdit', {
                description: 'Vous n\'avez pas les permissions d\'administrateur.'
            });
            navigate('/dashboard');
            setChecking(false);
            return;
        }

        setChecking(false);
    }, [authLoading, userId, rolePending, role, isError, navigate]);

    if (authLoading || checking || (userId && rolePending)) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
                    <p className="text-sm text-muted-foreground">Vérification des permissions...</p>
                </div>
            </div>
        );
    }

    if (!userId || isError || !isPlatformAdminRole(role)) {
        return null;
    }

    return <>{children}</>;
}
